/**
 * Word-level diff for the Languages compare view: what an English VARIANT (en-GB) changed in the
 * en-US source it was made from (Gray → Grey, vapor → vapour). Tokens are words, punctuation and
 * whitespace, matched with an LCS so unchanged words line up; a run of removed + added words
 * between two matches becomes one `changed` segment carrying what it `was`. Pure, no dependency.
 */

export type DiffSegment =
  /** Present in both. */
  | { kind: 'same'; text: string }
  /** Replaces `was` from the source. */
  | { kind: 'changed'; text: string; was: string }
  /** New in the target. */
  | { kind: 'added'; text: string }
  /** In the source, dropped from the target (rendered struck through). */
  | { kind: 'removed'; text: string };

export interface WordDiff {
  segments: DiffSegment[];
  /** Words touched — per change, the larger of the removed and added word counts. */
  changedWords: number;
}

// Words (letters/digits, keeping in-word ' ’ -), single punctuation marks, whitespace runs.
const TOKEN = /\s+|[\p{L}\p{N}'’-]+|[^\s\p{L}\p{N}]/gu;
const WORD = /[\p{L}\p{N}]/u;
/** LCS is O(n·m); above this many cells fall back to a whole-text comparison. */
const MAX_CELLS = 250_000;

function tokenize(text: string): string[] {
  return text.match(TOKEN) ?? [];
}

function countWords(tokens: readonly string[]): number {
  return tokens.filter((t) => WORD.test(t)).length;
}

/** Diff `target` against `source`, word by word. */
export function diffWords(source: string, target: string): WordDiff {
  if (source === target) return { segments: [{ kind: 'same', text: target }], changedWords: 0 };
  const a = tokenize(source);
  const b = tokenize(target);
  if (a.length * b.length > MAX_CELLS) {
    return {
      segments: [{ kind: 'changed', text: target, was: source }],
      changedWords: Math.max(countWords(a), countWords(b)),
    };
  }

  // lcs[i * width + j] = LCS length of a[i..] and b[j..].
  const width = b.length + 1;
  const lcs = new Uint32Array((a.length + 1) * width);
  for (let i = a.length - 1; i >= 0; i--) {
    for (let j = b.length - 1; j >= 0; j--) {
      lcs[i * width + j] =
        a[i] === b[j]
          ? (lcs[(i + 1) * width + j + 1] ?? 0) + 1
          : Math.max(lcs[(i + 1) * width + j] ?? 0, lcs[i * width + j + 1] ?? 0);
    }
  }

  const segments: DiffSegment[] = [];
  let changedWords = 0;
  let removed: string[] = [];
  let added: string[] = [];

  // Merge adjacent unchanged text so the renderer gets few, long segments.
  const push = (segment: DiffSegment) => {
    const last = segments[segments.length - 1];
    if (last?.kind === 'same' && segment.kind === 'same') last.text += segment.text;
    else segments.push(segment);
  };
  // Close the pending run of removed/added tokens into one segment; whitespace-only is not a change.
  const flush = () => {
    const was = removed.join('');
    const text = added.join('');
    if (was.trim() === '' && text.trim() === '') {
      if (text) push({ kind: 'same', text });
    } else {
      changedWords += Math.max(countWords(removed), countWords(added));
      if (!text.trim()) {
        push({ kind: 'removed', text: was });
      } else {
        // Keep edge whitespace outside the mark so only the words are underlined.
        const core = text.trim();
        const lead = text.slice(0, text.indexOf(core));
        const trail = text.slice(lead.length + core.length);
        if (lead) push({ kind: 'same', text: lead });
        push(was.trim() ? { kind: 'changed', text: core, was } : { kind: 'added', text: core });
        if (trail) push({ kind: 'same', text: trail });
      }
    }
    removed = [];
    added = [];
  };

  let i = 0;
  let j = 0;
  while (i < a.length || j < b.length) {
    const ai = a[i];
    const bj = b[j];
    if (ai !== undefined && ai === bj) {
      flush();
      push({ kind: 'same', text: bj });
      i++;
      j++;
    } else if (
      bj === undefined ||
      (ai !== undefined && (lcs[(i + 1) * width + j] ?? 0) >= (lcs[i * width + j + 1] ?? 0))
    ) {
      removed.push(ai ?? '');
      i++;
    } else {
      added.push(bj);
      j++;
    }
  }
  flush();
  return { segments, changedWords };
}
